// Precondition: None
// Postcondition: Custom element class for 3D button component with size variants (small, normal, large) and icon variant

interface TypeStyles {
  edge: string;
  front: string;
}

class FtButton extends HTMLElement {
  private abortController = new AbortController();

  static get observedAttributes(): string[] {
    return ['type', 'size', 'variant'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  // Precondition: Attribute in observedAttributes changed
  // Postcondition: Re-renders component with new styles
  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue && this.shadowRoot?.children.length) {
      this.render();
    }
  }

  // Precondition: Shadow DOM attached
  // Postcondition: Renders HTML structure into shadow DOM and attaches event listeners
  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.setupEventListeners();
    }
  }

  // Precondition: None
  // Postcondition: Returns CSS string for shadow DOM with 3D effect styles
  private getStyles(): string {
    const size = this.getAttribute('size') || 'normal';
    const type = this.getAttribute('type') || 'primary';
    const variant = this.getAttribute('variant') || 'text';
    const sizeStyles = this.getSizeStyles(size, variant);
    const typeStyles = this.getTypeStyles(type);

    return `
      :host {
        display: inline-block;
      }

      .pushable {
        position: relative;
        border: none;
        background: transparent;
        padding: 0;
        cursor: pointer;
        outline-offset: 4px;
        transition: filter 250ms;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
      }

      .pushable:focus {
        outline: var(--focus, 2px solid #1C1C1E);
      }

      .pushable:focus:not(:focus-visible) {
        outline: none;
      }

      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius, 8px);
        background: hsl(0deg 0% 0% / 0.25);
        filter: blur(4px);
        will-change: transform;
        transform: translateY(2px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      .edge {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius, 8px);
        ${typeStyles.edge}
      }

      .front {
        display: block;
        position: relative;
        border-radius: var(--radius, 8px);
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: inherit;
        will-change: transform;
        transform: translateY(-4px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
        ${sizeStyles}
        ${typeStyles.front}
      }

      .pushable:hover {
        filter: brightness(110%);
      }

      .pushable:hover .front {
        transform: translateY(-6px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      .pushable:active .front {
        transform: translateY(-2px);
        transition: transform 34ms;
      }

      .pushable:hover .shadow {
        transform: translateY(4px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      .pushable:active .shadow {
        transform: translateY(1px);
        transition: transform 34ms;
      }

      @media (prefers-reduced-motion: reduce) {
        .front,
        .shadow {
          transition: none;
        }
      }

      @media (prefers-color-scheme: dark) {
        .pushable:focus {
          outline: var(--focus, 2px solid #ffffff);
        }
      }

      /* Icon variant styles */
      ::slotted(svg) {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .front svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
    `;
  }

  // Precondition: type is one of 'primary', 'secondary'
  // Postcondition: Returns object with edge and front CSS strings
  private getTypeStyles(type: string): TypeStyles {
    switch (type) {
      case 'secondary':
        return {
          edge: `
            background: linear-gradient(
              to left,
              hsl(220deg 13% 60%) 0%,
              hsl(220deg 13% 75%) 8%,
              hsl(220deg 13% 75%) 92%,
              hsl(220deg 13% 60%) 100%
            );

            @media (prefers-color-scheme: dark) {
              background: linear-gradient(
                to left,
                hsl(220deg 13% 25%) 0%,
                hsl(220deg 13% 35%) 8%,
                hsl(220deg 13% 35%) 92%,
                hsl(220deg 13% 25%) 100%
              );
            }
          `,
          front: `
            background: var(--bg, #f3f4f6);
            border: 1px solid var(--border, #e5e7eb);
            color: var(--text, #1f2937);

            @media (prefers-color-scheme: dark) {
              background: var(--bg, #374151);
              border-color: var(--border, #4b5563);
              color: var(--text, #f3f4f6);
            }
          `
        };
      case 'primary':
      default:
        return {
          edge: `
            background: linear-gradient(
              to left,
              hsl(2deg 70% 35%) 0%,
              hsl(2deg 70% 50%) 8%,
              hsl(2deg 70% 50%) 92%,
              hsl(2deg 70% 35%) 100%
            );
          `,
          front: `
            background: var(--primary, #E0524D);
            border: 0;
            color: var(--primary-text, #ffffff);
          `
        };
    }
  }

  // Precondition: size is one of 'small', 'normal', 'large'; variant is 'text' or 'icon'
  // Postcondition: Returns CSS string with size-specific properties
  // Uses global height variables: --height-sm, --height-md, --height-lg
  private getSizeStyles(size: string, variant: string): string {
    if (variant === 'icon') {
      switch (size) {
        case 'small':
          return `
            width: var(--height-sm, 32px);
            height: var(--height-sm, 32px);
            padding: 0;
          `;
        case 'large':
          return `
            width: var(--height-lg, 48px);
            height: var(--height-lg, 48px);
            padding: 0;
          `;
        case 'normal':
        default:
          return `
            width: var(--height-md, 40px);
            height: var(--height-md, 40px);
            padding: 0;
          `;
      }
    }

    switch (size) {
      case 'small':
        return `
          padding: 0 12px;
          font: 700 12px/1 Inter, system-ui, sans-serif;
          height: var(--height-sm, 32px);
          min-width: 64px;
        `;
      case 'large':
        return `
          padding: 0 24px;
          font: 700 16px/1 Inter, system-ui, sans-serif;
          height: var(--height-lg, 48px);
          min-width: 96px;
        `;
      case 'normal':
      default:
        return `
          padding: 0 16px;
          font: 700 14px/1 Inter, system-ui, sans-serif;
          height: var(--height-md, 40px);
          min-width: 80px;
        `;
    }
  }

  // Precondition: None
  // Postcondition: Returns HTML template string with 3D layered structure
  private getTemplate(): string {
    const variant = this.getAttribute('variant') || 'text';
    const label = this.getAttribute('label') || this.textContent || 'Button';
    const defaultContent = variant === 'icon' ? '' : label;
    return `
      <button class="pushable" type="button" aria-label="${label}">
        <span class="shadow"></span>
        <span class="edge"></span>
        <span class="front"><slot>${defaultContent}</slot></span>
      </button>
    `;
  }

  // Precondition: Shadow DOM rendered
  // Postcondition: Event listeners attached to button
  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const button = this.shadowRoot?.querySelector('button');
    if (!button) return;

    button.addEventListener('click', (e) => {
      const href = this.getAttribute('href');

      // If href is set, navigate to the URL
      if (href) {
        e.preventDefault();
        window.location.href = href;
        return;
      }

      // Otherwise dispatch custom event when clicked to allow parent to handle action
      this.dispatchEvent(
        new CustomEvent('button-click', {
          detail: { value: this.getAttribute('value') || this.textContent },
          bubbles: true,
          composed: true,
        })
      );
    }, { signal });
  }

  // Precondition: None
  // Postcondition: Returns the button's associated value
  getValue(): string {
    return this.getAttribute('value') || this.textContent || '';
  }

  // Precondition: value is a string
  // Postcondition: Sets the button's associated value
  setValue(value: string): void {
    this.setAttribute('value', value);
  }

  // Precondition: None
  // Postcondition: Focuses the button element
  focus(): void {
    this.shadowRoot?.querySelector('button')?.focus();
  }

  // Precondition: None
  // Postcondition: Returns true if button currently focused
  isFocused(): boolean {
    return this.shadowRoot?.activeElement === this.shadowRoot?.querySelector('button');
  }
}


// Register custom element with guard
if (!customElements.get('ft-button')) {
  customElements.define('ft-button', FtButton);
}
